import { DateTime, Duration } from 'luxon';
import { HourReading } from '../hour-reading/hour-reading';
import { PaginationRequest } from '../pagination/pagination';
import { DurationUnit } from '../pricing/pricing';
import { OrderType } from '../order/order';

export class ProductInventoryUnit {
  id!: number;
  productId!: number;
  locationId!: number;
  unitNumber!: string;
  serialNumber!: string;
  qrCode?: string;
  purchasePrice?: number;
  isOnOutage!: boolean;
  isActive!: boolean;
  createdAt!: DateTime;
  updatedAt!: DateTime;
  deletedAt?: DateTime;
  lastHourReading?: HourReading;
  classification?: ProductInventoryUnitTaxonomyClassification;
  forRent!: boolean;
  forSale!: boolean;
  purchasedAt!: DateTime;
  soldAt?: DateTime;
  boomAndBucketEquipmentId?: string;
}

export enum VerificationStatus {
  Unset = 0,
  Unverified = 1,
  SystemProcessed = 2,
  VerifiedByExpert = 4,
  VerifiedByAi = 8,
  QuipliCustom = 16,
}
export class Make {
  readonly id!: string;
  readonly name!: string;
  readonly displayName!: string;
  readonly url?: string;
  readonly verificationStatus?: VerificationStatus;
  readonly aliases?: string[];
}

export enum MeterType {
  EngineOperatingTime = 'Engine Operating Time',
  EngineIdleTime = 'Engine Idle Time',
  Odometer = 'Odometer',
  FrameTime = 'Frame Time',
  ImplementOperatingTime = 'Implement Operating Time',
}

export class TaxonomyCategory {
  readonly id!: string;
  readonly slug!: string;
  readonly nameEn!: string;
  readonly nameEs?: string;
  readonly nameFr?: string;
  readonly descriptionEn!: string;
  readonly descriptionEs?: string;
  readonly descriptionFr?: string;
  readonly meterType?: MeterType; //subcategories only
  readonly parentCategory?: TaxonomyCategory;
}
export class Model {
  readonly id!: string;
  readonly name!: string;
  readonly displayName!: string;
  readonly verificationStatus?: VerificationStatus;
  readonly yearsManufactured?: number[];
  readonly subcategories!: TaxonomyCategory[];
}

export type ProductInventoryUnitTaxonomyClassification = {
  make: Make;
  model: Model;
  year: number;
};

export type UpdateProductUnitRequest = Omit<
  ProductInventoryUnit,
  'id' | 'productId' | 'createdAt' | 'updatedAt' | 'deletedAt' | 'lastHourReading'
> & {
  hourReading?: number;
  modelYear?: { modelId: string; year: number };
};

export type UpsertPartServiceRequest = {
  name: string;
  id?: number;
  sku?: string;
  locationIds?: number[];
  chargeTax?: boolean;
  taxCategoryId?: string | undefined;
  incomeGlAccountId?: string | undefined;
  qboAccountId?: string | undefined;
  purchasePrice?: number | undefined;
  type: 'Part' | 'Service';
  categoryIds?: number[];
  isActive?: boolean;
};

export enum ProductTrackingType {
  SerialUnits = 'SerialUnits',
  Quantity = 'Quantity',
  NonInventory = 'NonInventory',
}

export class ProductLocationStock {
  productId!: number;
  locationId!: number;
  rentalStock!: number;
  saleStock!: number;
}

export enum ProductType {
  Product = 'Product',
  Custom = 'Custom',
  Part = 'Part',
  Service = 'Service',
}

export class Product {
  id!: number;
  name!: string;
  isActive!: boolean;
  isDeleted!: boolean;
  allowRent!: boolean;
  allowSale!: boolean;
  trackingType!: ProductTrackingType;
  minRentalDuration!: Duration;
  maxRentalDuration?: Duration;
  locationIds!: number[];
  categoryIds!: number[];
  rentalStock!: number;
  saleStock!: number;
  totalStock!: number;
  inventoryUnits!: ProductInventoryUnit[];
  disabledStartDate?: DateTime;
  disabledEndDate?: DateTime;
  allowHourTracking!: boolean;
  fuelChargeProductId?: number;
  purchasePrice?: number;
  outageStock!: number;
  sku?: string;
  taxable!: boolean;
  stock?: ProductLocationStock[];
  defaultImage?: ProductImage;
  type!: ProductType;
  createdAt!: DateTime;
  seisRdw?: boolean;
  defaultVendorId?: number;
}

export type ProductUpdateRequest = {
  isActive?: boolean;
  isDeleted?: boolean;
};

export class UpdateProductLocationStockRequest {
  productId!: number;
  rentalStock?: number;
  saleStock?: number;
  locationStocks?: ProductLocationStock[];
}

export class ProductListRequest extends PaginationRequest {
  trackingType?: ProductTrackingType;
  keyword?: string;
  categoryId?: number;
  locationId?: number;
  isActive?: boolean;
  productFor?: OrderType;
  createdFrom?: DateTime;
  createdTo?: DateTime;
}

export class SearchProductInventoryUnitsRequest extends PaginationRequest {
  keyword?: string;
  sort?: {
    field: 'unitNumber' | 'serialNumber' | 'category' | 'location' | 'status' | 'createdAt' | 'productId';
    direction?: 'asc' | 'desc';
  };
  categoryIds?: number[];
  locationIds?: number[];
  productIds?: number[];
  forRent?: boolean;
  forSale?: boolean;
  includeSoldUnits?: boolean;
  keepSoldBeforeFromDate?: boolean;
}

export class ProductImage {
  id!: string;
  productId!: number;
  filename!: string;
  uploadedBy!: number;
  uploadedAt!: DateTime;
  rank!: number;
  altTitle?: string;
}

export enum ProductImageSize {
  Original = 'ORIGINAL',
  Mini = 'MINI',
  Thumbnail = 'THUMB',
  Medium = 'MEDIUM',
  Large = 'LARGE',
}

export type HourTrackingConfig = {
  productId: number;
  dailyHoursLimit: number;
  hourlyOverageRate?: number;
};

export type FuelChargingConfig = {
  productId: number;
  fuelChargeProductId: number;
};

export type UpsertMinimumDurationPriceRequest = {
  productId: number;
  durationValue: number;
  durationUnit: DurationUnit;
};

export type MinimumDurationPrice = {
  id: string;
  productId: number;
  durationValue: number;
  durationUnit: DurationUnit;
  updatedAt: DateTime;
};
export type ProductsSortBy = 'name' | 'default' | 'relevance' | 'featured' | 'createdAt';

export type SearchProductsRequest = {
  types?: ProductType[];
  trackingType?: ProductTrackingType;
  keyword?: string;
  categoryIds?: number[];
  locationIds?: number[];
  isActive?: boolean; //'Yes' | 'No';
  createdFrom?: string;
  createdTo?: string;
  pageSize?: number;
  pageNumber?: number;
  isRental?: boolean;
  isSale?: boolean;
  sortBy?: ProductsSortBy;
  sortDirection?: 'asc' | 'desc';
  // new fields for category filter groups...
  filterIds?: number[];
};

export type CalculateOverageChargeRequest = {
  orderId: number;
  orderProductId: number;
  unitId: number;
  start: DateTime;
  end: DateTime;
  hoursIn: number;
};

export type CalculateOverageChargeResult = CalculateOverageChargeRequest & {
  dailyHoursLimit: number;
  hourlyOverageRate: number;
  overageHours: number;
  overageCharge: number;
};

export type ValuationConfidenceScore = 'Low' | 'Medium' | 'High';
export type ProductUnitValuation = {
  id: string;
  productId: number;
  productUnitId: number;
  valuationTimestamp: DateTime;
  make: string;
  model: string;
  year: number;
  meter: number;
  taxonomizedSubcategorySlug: string;
  retailConfidenceIntervalHighPriceInCents: number;
  retailConfidenceIntervalLowPriceInCents: number;
  retailEstimatedPriceInCents: number;
  auctionConfidenceIntervalHighPriceInCents: number;
  auctionConfidenceIntervalLowPriceInCents: number;
  auctionEstimatedPriceInCents: number;
  retailConfidencePercentage: number;
  auctionConfidencePercentage: number;
  retailComparableCount: number;
  auctionComparableCount: number;
  retailConfidenceScore: ValuationConfidenceScore;
  auctionConfidenceScore: ValuationConfidenceScore;
  retailMinPriceInCents?: number;
  retailMaxPriceInCents?: number;
  auctionMinPriceInCents?: number;
  auctionMaxPriceInCents?: number;
};
export enum HighwayType {
  OnHighway = 1,
  OffHighway = 2,
}

export class ListOnBoomAndBucketRequest {
  productId!: number;
  productUnitId!: number;
  hours!: number;
  mileage!: number;
  highwayType!: HighwayType;
  sellerCompanyName!: string;
  sellerOfficeAddressLineOne!: string;
  sellerOfficeAddressLineTwo?: string;
  sellerOfficeCity!: string;
  sellerOfficeState!: string;
  sellerOfficeZipCode!: string;
  sellerContactFirstName!: string;
  sellerContactLastName!: string;
  sellerContactEmail!: string;
  sellerContactPhone!: string;
}

export class GetEquipmentResponse {
  // used to fetch the listingId tied to the onboarded equipment (equipmentId returned as response from onboard equipment api)
  id!: string;
  equipmentCategory!: string;
  equipmentCategorySlug!: string;
  hours?: number;
  mileage?: number;
  make!: string;
  model!: string;
  image?: string; //url
  year!: number;
  totalListingCount!: number;
  activeListingCount!: number;
  listings!: string[]; //all listing id's of this equipment id
  serialNumbers!: {
    id: string;
    type: number; // no idea the types..
    description?: string;
    serialNumber: string;
  }[];
  equipmentFeatures!: any[];
  dimensions?: string;
  weight?: string;
  activeListing?: string;
  crmEquipmentId?: any;
  urlSlug!: string;
  slug!: string;
  modelSlug!: string;
  makeSlug!: string;
  condition?: number;
  highwayType?: HighwayType;
  taxonomizedSubCategoryId!: string;
}
export enum DisplayState {
  NotDisplayed,
  OnMarketplace,
  OnMarketplaceAsInactive,
}
export class GetListingResponse {
  id!: string;
  workflowState!: number;
  displayState!: DisplayState;
  equipmentId!: string;
  dateTimeUtcActiveOnMarketplaceEmailSent?: DateTime;
  dateTimeUtcWentOnMarketplace?: DateTime;
  dateTimeUtcWentOffMarketplace?: DateTime;
  //   priceStrategyType?: number; //??
  //   originalEquipmentCostInCents?: number;
  //   fleetAdvisorId?: string;
  //   buyerId?: string;
  //   buyerCompanyId?: string;
  //   sellerCompanyId?: string;
  //   listingCompanyId?: string;
  //   stockNumber!: string;
  //   totalViewcount!: number;
  //   marketplaceViewCount!: number;
}

export type EquipmentListingResponse = GetEquipmentResponse & { activeListing?: GetListingResponse };

export type ProductDepreciationPercentage = {
  id: number;
  productId: number;
  depreciationPercentage: number;
  createdAt: DateTime;
};
